// Anything that does not change between frames is drawn once into its own canvas and copied after
// that. This is what lets the art carry real detail — fine hatching, planks, foliage — and still
// run on a phone: the strokes are paid for once, the frames only cost a copy.
/** how many device pixels one world unit gets when baking; the renderer sets this on resize */
let spriteS = 2;
// Keys carry their own scale, so a change of render scale does not throw the cache away —
// the stale entries simply age out. Clearing here caused a storm of re-bakes every time the
// quality governor moved a step.
export const setSpriteScale = (v: number) => { spriteS = v; };
export const spriteScale = () => spriteS;

const cache = new Map<string, HTMLCanvasElement>();
const order: string[] = [];
const LIMIT = 400;
// Canvases are the expensive resource here, not entries: a baked ground tile is a thousand pixels
// square. The cache is held to a pixel budget so a walk across the page cannot quietly allocate
// a few hundred megabytes on a phone.
const PIXEL_BUDGET = 26e6;
let pixels = 0;

export interface Baked { canvas: HTMLCanvasElement; ox: number; oy: number; w: number; h: number }
const boxes = new Map<string, Baked>();

/**
 * Bake a drawing into a canvas. The draw function works in world units with the origin at (0,0)
 * of the given box, which may extend into negatives (a character is drawn above its feet).
 */
export function bake(
  key: string,
  box: { x: number; y: number; w: number; h: number },
  scale: number,
  draw: (ctx: CanvasRenderingContext2D) => void,
): Baked | null {
  const full = `${key}@${scale.toFixed(2)}`;
  const hit = boxes.get(full);
  if (hit) { touch(full); return hit; }
  const cv = document.createElement('canvas');
  cv.width = Math.max(1, Math.ceil(box.w * scale));
  cv.height = Math.max(1, Math.ceil(box.h * scale));
  bakeMisses++;
  const c = cv.getContext('2d');
  if (!c) return null;
  c.scale(scale, scale);
  c.translate(-box.x, -box.y);
  draw(c);
  const baked: Baked = { canvas: cv, ox: box.x, oy: box.y, w: box.w, h: box.h };
  cache.set(full, cv); boxes.set(full, baked); order.push(full);
  pixels += cv.width * cv.height;
  while (order.length > LIMIT || (pixels > PIXEL_BUDGET && order.length > 1)) {
    const old = order.shift();
    if (!old) break;
    const gone = cache.get(old);
    if (gone) { pixels -= gone.width * gone.height; gone.width = gone.height = 0; }
    cache.delete(old); boxes.delete(old); evictions++;
  }
  return baked;
}

function touch(key: string) {
  const i = order.indexOf(key);
  if (i > 0) { order.splice(i, 1); order.push(key); }
}

/** draw a baked image with its origin at (x, y) in world units */
export function blit(ctx: CanvasRenderingContext2D, b: Baked, x: number, y: number) {
  ctx.drawImage(b.canvas, x + b.ox, y + b.oy, b.w, b.h);
}

/** look a baked image up without drawing it, so a caller can spread its work over frames */
export function baked(key: string, scale: number): Baked | null {
  const full = `${key}@${scale.toFixed(2)}`;
  const hit = boxes.get(full);
  if (hit) { touch(full); return hit; }
  return null;
}

export function clearSprites() { cache.clear(); boxes.clear(); order.length = 0; pixels = 0; }
export const spriteCount = () => order.length;
export let bakeMisses = 0;
export const readMisses = () => { const v = bakeMisses; bakeMisses = 0; return v; };
export let evictions = 0;
export const readEvictions = () => { const v = evictions; evictions = 0; return v; };
export const cachePixels = () => pixels;
